import { buildApiUrl } from '../utils/apiConfig';
import { redirectToLogin } from '../utils/auth';

export interface MediaKitPhoto {
    id: number;
    imageUrl: string;
    displayOrder: number;
}

export interface MediaKitHighlight {
    id: number;
    title: string | null;
    description: string | null;
    impact: string | null;
    displayOrder: number;
}

export interface MediaKitSocials {
    instagramHandle?: string | null;
    tiktokHandle?: string | null;
    linkedinUrl?: string | null;
    facebookName?: string | null;
    hudlUrl?: string | null;
    xHandle?: string | null;
}

export interface MediaKitData {
    firstName: string;
    lastName: string;
    graduationYear?: number | null;
    sport?: string | null;
    aboutYou?: string | null;
    photos: MediaKitPhoto[];
    socials: MediaKitSocials;
    selectedInterests: string[];
    customInterests?: string | null;
    introVideoUrl?: string | null;
    highlights: MediaKitHighlight[];
    completed: boolean;
    athleteUserId: number;
}

export interface InterestCategory {
    id: number;
    name: string;
    interests: { id: number; name: string }[];
}

export interface MediaKitPublicData {
    firstName: string;
    lastName: string;
    graduationYear?: number | null;
    sport?: string | null;
    email?: string | null;
    aboutYou?: string | null;
    photos: MediaKitPhoto[];
    socials: MediaKitSocials;
    interests: string[];
    introVideoUrl?: string | null;
    highlights: MediaKitHighlight[];
    profileImageUrl?: string | null;
}

function athleteUserIdQuery(athleteUserId?: number): string {
    return athleteUserId != null ? `?athleteUserId=${athleteUserId}` : '';
}

async function handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
        let message = 'Request failed';
        try {
            const data = await response.json();
            message = data.message || data.detail || message;
        } catch {
            // ignore
        }
        if (response.status === 401 || response.status === 403) {
            redirectToLogin(true);
            throw new Error(message);
        }
        throw new Error(message);
    }
    return response.json();
}

export async function fetchMediaKit(athleteUserId?: number): Promise<MediaKitData> {
    const response = await fetch(buildApiUrl(`api/media-kit/me${athleteUserIdQuery(athleteUserId)}`), {
        credentials: 'include',
        headers: { Accept: 'application/json' },
    });
    return handleResponse<MediaKitData>(response);
}

export async function saveMediaKitBasics(graduationYear: number, athleteUserId?: number): Promise<void> {
    const response = await fetch(buildApiUrl(`api/media-kit/me/basics${athleteUserIdQuery(athleteUserId)}`), {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ graduationYear }),
    });
    await handleResponse(response);
}

export async function saveMediaKitAbout(aboutYou: string, athleteUserId?: number): Promise<void> {
    const response = await fetch(buildApiUrl(`api/media-kit/me/about${athleteUserIdQuery(athleteUserId)}`), {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ aboutYou }),
    });
    await handleResponse(response);
}

export async function saveMediaKitSocials(socials: MediaKitSocials, athleteUserId?: number): Promise<void> {
    const response = await fetch(buildApiUrl(`api/media-kit/me/socials${athleteUserIdQuery(athleteUserId)}`), {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(socials),
    });
    await handleResponse(response);
}

export async function fetchInterestCatalog(): Promise<InterestCategory[]> {
    const response = await fetch(buildApiUrl('api/media-kit/interests'), {
        credentials: 'include',
        headers: { Accept: 'application/json' },
    });
    return handleResponse<InterestCategory[]>(response);
}

export async function saveMediaKitInterests(
    interestIds: number[],
    customInterests: string,
    athleteUserId?: number
): Promise<void> {
    const response = await fetch(buildApiUrl(`api/media-kit/me/interests${athleteUserIdQuery(athleteUserId)}`), {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ interestIds, customInterests }),
    });
    await handleResponse(response);
}

export async function saveMediaKitIntroVideo(introVideoUrl: string, athleteUserId?: number): Promise<void> {
    const response = await fetch(buildApiUrl(`api/media-kit/me/intro-video${athleteUserIdQuery(athleteUserId)}`), {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ introVideoUrl }),
    });
    await handleResponse(response);
}

export async function addMediaKitHighlight(
    highlight: { title?: string; description?: string; impact?: string },
    athleteUserId?: number
): Promise<MediaKitHighlight> {
    const response = await fetch(buildApiUrl(`api/media-kit/me/highlights${athleteUserIdQuery(athleteUserId)}`), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(highlight),
    });
    return handleResponse<MediaKitHighlight>(response);
}

export async function completeMediaKit(athleteUserId?: number): Promise<MediaKitData> {
    const response = await fetch(buildApiUrl(`api/media-kit/me/complete${athleteUserIdQuery(athleteUserId)}`), {
        method: 'PUT',
        credentials: 'include',
        headers: { Accept: 'application/json' },
    });
    return handleResponse<MediaKitData>(response);
}

export async function updateMediaKit(
    update: Partial<{
        aboutYou: string;
        socials: MediaKitSocials;
        interestIds: number[];
        customInterests: string;
        introVideoUrl: string;
        highlights: { title?: string; description?: string; impact?: string }[];
    }>,
    athleteUserId?: number
): Promise<MediaKitData> {
    const response = await fetch(buildApiUrl(`api/media-kit/me${athleteUserIdQuery(athleteUserId)}`), {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(update),
    });
    return handleResponse<MediaKitData>(response);
}

export async function deleteMediaKitPhoto(photoId: number, athleteUserId?: number): Promise<void> {
    const response = await fetch(
        buildApiUrl(`api/media-kit/me/photos/${photoId}${athleteUserIdQuery(athleteUserId)}`),
        { method: 'DELETE', credentials: 'include' }
    );
    if (response.status === 401 || response.status === 403) {
        redirectToLogin(true);
        throw new Error('Unauthorized');
    }
    if (!response.ok) {
        throw new Error('Failed to delete photo');
    }
}

export function buildMediaKitPhotoUploadUrl(athleteUserId?: number): string {
    return buildApiUrl(`api/media-kit/me/photos${athleteUserIdQuery(athleteUserId)}`);
}

export async function fetchMediaKitShareLink(athleteUserId: number): Promise<string> {
    const response = await fetch(buildApiUrl(`api/media-kit/link?athleteUserId=${athleteUserId}`), {
        credentials: 'include',
        headers: { Accept: 'application/json' },
    });
    const data = await handleResponse<{ shareableLink: string }>(response);
    return data.shareableLink;
}

export async function shareMediaKit(
    athleteUserId: number,
    emails: string[],
    message?: string
): Promise<void> {
    const response = await fetch(buildApiUrl(`api/media-kit/share?athleteUserId=${athleteUserId}`), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ emails, message }),
    });
    await handleResponse(response);
}

export async function fetchPublicMediaKit(athleteName: string, uuid: string): Promise<MediaKitPublicData> {
    const response = await fetch(
        buildApiUrl(`api/media-kit/shared/${encodeURIComponent(athleteName)}/${uuid}`),
        { headers: { Accept: 'application/json' } }
    );
    return handleResponse<MediaKitPublicData>(response);
}

export function buildPublicMediaKitPhotoUrl(uuid: string, photoId: number): string {
    return buildApiUrl(`api/media-kit/shared/${encodeURIComponent(uuid)}/photos/${photoId}`);
}

export function isValidIntroVideoUrl(url: string): boolean {
    if (!url.trim()) return true;
    try {
        const parsed = new URL(url.trim());
        const host = parsed.hostname.toLowerCase();
        return host.includes('youtube.com') || host.includes('youtu.be') || host.includes('vimeo.com');
    } catch {
        return false;
    }
}

export function hasHighlightFields(title: string, description: string, impact: string): boolean {
    return Boolean(title.trim() || description.trim() || impact.trim());
}
